import cn from 'classnames'
import React from 'react'
import { useTranslate } from '../src/hooks/useTranslate'
import { ImageFragment, Model3dFragment } from '../src/model/fragments'
import SvgModel3d from './icons/SvgModel3d'
import { Picture, PictureFit } from './Picture'
import { Viewport3d } from './Viewport3d'

type GalleryItemPicture = (ImageFragment & { cover?: boolean });
export type GalleryItem = GalleryItemPicture | Model3dFragment

export interface GalleryProps {
	items: GalleryItem[],
}

export const Gallery: React.FunctionComponent<GalleryProps> = ({ items }) => {
	const [currentItem, setCurrentItem] = React.useState(0)
	const translate = useTranslate();	
	const currentItemCapped = Math.min(currentItem, items.length - 1);

	const isPicture = React.useCallback((item: GalleryItem) => {
		return !(item as Model3dFragment).geometry;
	}, []);

	const isModel = React.useCallback((item: GalleryItem) => {
		return !isPicture(item);
	}, [isPicture]);

	const picture = React.useMemo(() => {
		return isPicture(items[currentItemCapped]) ? items[currentItemCapped] as GalleryItemPicture : null;
	}, [items, currentItemCapped, isPicture]);

	const model = React.useMemo(() => {
		return isModel(items[currentItemCapped]) ? items[currentItemCapped] as Model3dFragment : null;
	}, [items, currentItemCapped, isModel]);
	
	return (
		<div className="gallery">
			<div className="gallery-stage">
				{
					picture && 
					<Picture
						key={currentItemCapped}
						alt=""
						image={picture}
						width={480!}
						height={700}
						layout={PictureFit.Heighten}
						mode={!picture.cover ? 'contain' : 'cover'}
					/>
				}
				{
					model && 
					<Viewport3d model={model} aspectRatio={478 / 697} />
				}
			</div>
			{items.length > 1 && (
				<>
					<div className="gallery-counter">
						{currentItemCapped + 1}/{items.length}
					</div>
					<div className="gallery-arrows">
						<button
							type="button"
							className="gallery-arrow"
							onClick={() => {
								setCurrentItem(
									(items.length + currentItemCapped - 1) % items.length,
								)
							}}
							karia-label={translate('previous')}
						/>
						<button
							type="button"
							className="gallery-arrow"
							onClick={() => {
								setCurrentItem((currentItemCapped + 1) % items.length)
							}}
							aria-label="Další"
						/>
					</div>
				</>
			)}
			<div className="gallery-thumbnails">
				{items.length > 1 && items.map((item, i) => (
					<button
						type="button"
						key={`${i}_${isPicture(item) ? (item as GalleryItemPicture).internalUrl : (item as Model3dFragment).geometry.id}`}
						className={cn(
							'gallery-thumbnail',
							i === currentItemCapped && 'is-current',
						)}
						onClick={() => setCurrentItem(i)}
					>
						{
							isPicture(item) && 
							<Picture
								alt=""
								image={item as GalleryItemPicture}
								width={55}
								height={55}
							/>
						}
						{
							isModel(item) &&
							<span  className='gallery-thumbnail-model-3d'>
							 	<SvgModel3d/> 
								<strong>
									3D
								</strong>
							</span>
						}
					</button>
				))}
			</div>
		</div>
	)
}
